import { useCallback, useEffect, useRef, useState } from 'react';

export interface UseGetResizePayload {
  initialLeftWidth: number;
  localStorageKey?: string;
}

export const useGetResize = ({ initialLeftWidth, localStorageKey }: UseGetResizePayload) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const isDragging = useRef(false);
  const [isResizing, setIsResizing] = useState(false);
  const [leftWidth, setLeftWidth] = useState(() => {
    if (localStorageKey) {
      const saved = localStorage.getItem(localStorageKey);
      return saved ? parseInt(saved, 10) : initialLeftWidth;
    }
    return initialLeftWidth;
  });

  useEffect(() => {
    if (localStorageKey) {
      localStorage.setItem(localStorageKey, leftWidth.toString());
    }
  }, [localStorageKey, leftWidth]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: one time useCallback that doesn't need dependencies
  const handleMouseUp = useCallback(() => {
    isDragging.current = false;
    setIsResizing(false);
    document.body.style.userSelect = '';
    document.removeEventListener('mousemove', handleMouseMove);
    document.removeEventListener('mouseup', handleMouseUp);
  }, []);

  const handleMouseMove = useCallback((e: MouseEvent) => {
    if (!isDragging.current || !containerRef.current) return;
    setIsResizing(true);
    const containerRect = containerRef.current.getBoundingClientRect();
    const newLeftWidth = e.clientX - containerRect.left;
    const minLeftWidth = 100;
    const maxWidth = containerRect.width - minLeftWidth;
    if (newLeftWidth > minLeftWidth && newLeftWidth < maxWidth) {
      setLeftWidth(newLeftWidth);
    }
  }, []);

  const handleMouseDown = useCallback(() => {
    isDragging.current = true;
    document.body.style.userSelect = 'none';
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  }, [handleMouseMove, handleMouseUp]);

  // Reset the left pane back to its initial width (wired to a double-click on the splitter).
  const handleReset = useCallback(() => {
    setLeftWidth(initialLeftWidth);
  }, [initialLeftWidth]);

  return {
    handleMouseDown,
    handleMouseMove,
    handleMouseUp,
    handleReset,
    leftWidth,
    containerRef,
    isResizing
  };
};
